import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useTheme } from "../../../context/ThemeContext";
import { useNavigate } from "react-router-dom";

type CouponType = "percentage" | "fixed";
type CouponScope = "pos" | "online";

interface CouponFormData {
  code: string;
  type: CouponType;
  value: number;
  minPurchase: number;
  usageLimit: number;
  startDate: string;
  endDate: string;
  scope: CouponScope[];
  description: string;
}

export default function CreateCouponSection() {
  const { t } = useTranslation("common");
  const { isDarkMode } = useTheme();
  const navigate = useNavigate();

  const [formData, setFormData] = useState<CouponFormData>({
    code: "",
    type: "percentage",
    value: 0,
    minPurchase: 0,
    usageLimit: 100,
    startDate: "",
    endDate: "",
    scope: ["pos", "online"],
    description: "",
  });

  const [isGeneratingCode, setIsGeneratingCode] = useState(false);

  const themeClasses = {
    bg: isDarkMode ? "bg-dark-surface" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-gray-50",
    inputBg: isDarkMode ? "bg-dark-surface" : "bg-white",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
  };

  const generateCode = () => {
    setIsGeneratingCode(true);
    const chars = "ABCDEFGHIJKLMNOPQRSTUVWXYZ0123456789";
    let code = "";
    for (let i = 0; i < 8; i++) {
      code += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData((prev) => ({ ...prev, code }));
    setTimeout(() => setIsGeneratingCode(false), 300);
  };

  const handleScopeToggle = (scope: CouponScope) => {
    setFormData((prev) => ({
      ...prev,
      scope: prev.scope.includes(scope)
        ? prev.scope.filter((s) => s !== scope)
        : [...prev.scope, scope],
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    navigate("/pos/coupons/list");
  };

  return (
    <div className="p-4 sm:p-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div>
          <h1 className={`text-2xl font-bold ${themeClasses.text}`}>
            {t("posCoupons.createCoupon")}
          </h1>
          <p className={themeClasses.textSecondary}>
            {t("posCoupons.createCouponSubtitle")}
          </p>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posCoupons.basicInfo")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.couponCode")}
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.code}
                  onChange={(e) => setFormData((prev) => ({ ...prev, code: e.target.value.toUpperCase() }))}
                  placeholder="WELCOME20"
                  className={`flex-1 px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
                <button
                  type="button"
                  onClick={generateCode}
                  disabled={isGeneratingCode}
                  className={`px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.buttonHover} ${themeClasses.text} transition-colors`}
                >
                  <i className={`ri-refresh-line ${isGeneratingCode ? "animate-spin" : ""}`}></i>
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.description")}
              </label>
              <input
                type="text"
                value={formData.description}
                onChange={(e) => setFormData((prev) => ({ ...prev, description: e.target.value }))}
                placeholder={t("posCoupons.descriptionPlaceholder")}
                className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posCoupons.discountConfig")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.discountType")}
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, type: "percentage" }))}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-colors ${
                    formData.type === "percentage"
                      ? "bg-primary-500 text-gray-900 border-primary-500"
                      : `${themeClasses.border} ${themeClasses.text}`
                  }`}
                >
                  <i className="ri-percent-line mr-2"></i>
                  {t("posCoupons.percentage")}
                </button>
                <button
                  type="button"
                  onClick={() => setFormData((prev) => ({ ...prev, type: "fixed" }))}
                  className={`flex-1 px-4 py-3 rounded-xl border transition-colors ${
                    formData.type === "fixed"
                      ? "bg-primary-500 text-gray-900 border-primary-500"
                      : `${themeClasses.border} ${themeClasses.text}`
                  }`}
                >
                  <i className="ri-money-dollar-circle-line mr-2"></i>
                  {t("posCoupons.fixedAmount")}
                </button>
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.discountValue")}
              </label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}>
                  {formData.type === "percentage" ? "%" : "$"}
                </span>
                <input
                  type="number"
                  value={formData.value}
                  onChange={(e) => setFormData((prev) => ({ ...prev, value: Number(e.target.value) }))}
                  min={0}
                  max={formData.type === "percentage" ? 100 : undefined}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.minPurchase")}
              </label>
              <div className="relative">
                <span className={`absolute left-4 top-1/2 -translate-y-1/2 ${themeClasses.textSecondary}`}>$</span>
                <input
                  type="number"
                  value={formData.minPurchase}
                  onChange={(e) => setFormData((prev) => ({ ...prev, minPurchase: Number(e.target.value) }))}
                  min={0}
                  className={`w-full pl-10 pr-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
                />
              </div>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posCoupons.validityAndUsage")}
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.startDate")}
              </label>
              <input
                type="date"
                value={formData.startDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, startDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.endDate")}
              </label>
              <input
                type="date"
                value={formData.endDate}
                onChange={(e) => setFormData((prev) => ({ ...prev, endDate: e.target.value }))}
                className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
            </div>

            <div>
              <label className={`block text-sm font-medium ${themeClasses.textSecondary} mb-2`}>
                {t("posCoupons.usageLimit")}
              </label>
              <input
                type="number"
                value={formData.usageLimit}
                onChange={(e) => setFormData((prev) => ({ ...prev, usageLimit: Number(e.target.value) }))}
                min={-1}
                placeholder={t("posCoupons.unlimitedHint")}
                className={`w-full px-4 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.inputBg} ${themeClasses.text} focus:outline-none focus:ring-2 focus:ring-primary-500`}
              />
              <p className={`text-xs ${themeClasses.textSecondary} mt-1`}>
                {t("posCoupons.useLimitHint")}
              </p>
            </div>
          </div>
        </div>

        <div className={`${themeClasses.bg} rounded-2xl border ${themeClasses.border} p-6`}>
          <h2 className={`text-lg font-semibold ${themeClasses.text} mb-4`}>
            {t("posCoupons.applicableChannels")}
          </h2>

          <div className="flex gap-4">
            <button
              type="button"
              onClick={() => handleScopeToggle("pos")}
              className={`flex-1 max-w-xs px-6 py-4 rounded-xl border-2 transition-all ${
                formData.scope.includes("pos")
                  ? "border-primary-500 bg-primary-500/10"
                  : `${themeClasses.border}`
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  formData.scope.includes("pos") ? "bg-primary-500" : themeClasses.cardBg
                }`}>
                  <i className={`ri-store-line text-xl ${formData.scope.includes("pos") ? "text-gray-900" : themeClasses.text}`}></i>
                </div>
                <div className="text-left">
                  <p className={`font-medium ${themeClasses.text}`}>POS</p>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posCoupons.pointOfSale")}</p>
                </div>
              </div>
            </button>

            <button
              type="button"
              onClick={() => handleScopeToggle("online")}
              className={`flex-1 max-w-xs px-6 py-4 rounded-xl border-2 transition-all ${
                formData.scope.includes("online")
                  ? "border-primary-500 bg-primary-500/10"
                  : `${themeClasses.border}`
              }`}
            >
              <div className="flex items-center gap-3">
                <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${
                  formData.scope.includes("online") ? "bg-primary-500" : themeClasses.cardBg
                }`}>
                  <i className={`ri-global-line text-xl ${formData.scope.includes("online") ? "text-gray-900" : themeClasses.text}`}></i>
                </div>
                <div className="text-left">
                  <p className={`font-medium ${themeClasses.text}`}>WEB</p>
                  <p className={`text-sm ${themeClasses.textSecondary}`}>{t("posCoupons.onlineStore")}</p>
                </div>
              </div>
            </button>
          </div>
        </div>

        <div className="flex justify-end gap-4">
          <button
            type="button"
            onClick={() => navigate("/pos/coupons/list")}
            className={`px-6 py-3 rounded-xl border ${themeClasses.border} ${themeClasses.text} font-medium transition-colors hover:bg-gray-500/10`}
          >
            {t("common.cancel")}
          </button>
          <button
            type="submit"
            className="px-6 py-3 rounded-xl bg-primary-500 text-gray-900 font-medium flex items-center gap-2 hover:bg-[#65eb3d] transition-colors"
          >
            <i className="ri-check-line"></i>
            {t("posCoupons.createCoupon")}
          </button>
        </div>
      </form>
    </div>
  );
}
